import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, CheckCircle2, Image as ImageIcon, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CardBlockSkeleton } from "@/components/LoadingSkeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Absolute URL for an image served through the /api endpoint. The API
// image endpoint requires auth — the httpOnly `access_token` cookie the
// backend set on login carries it. `credentials: "include"` on fetch()
// forwards the cookie, so no header manipulation (and no localStorage
// token read) is needed.
function AuthedImg({ url, alt }) {
  const [src, setSrc] = React.useState(null);
  const prevUrlRef = React.useRef(null);
  React.useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    fetch(`${BACKEND_URL}${url}`, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((b) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(b);
        // Revoke the previous blob URL (if any) before swapping to the new one
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = objUrl;
        setSrc(objUrl);
      })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => {
      cancelled = true;
      if (objUrl && objUrl !== prevUrlRef.current) URL.revokeObjectURL(objUrl);
    };
  }, [url]);
  // Final unmount cleanup
  React.useEffect(() => () => {
    if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
  }, []);
  if (!src) return <div className="h-24 w-full bg-slate-100 animate-pulse rounded" />;
  return <img src={src} alt={alt} className="w-full h-24 object-cover rounded border border-rose-200" />;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * IssueTaskBanner — the shaking soft-red banner pinned to the top of the
 * spreadsheet for a single task with open issues. Users can:
 *   • Expand the accordion to read the description
 *   • Click the "Resolved" button which opens a resolution note dialog
 *   • Open the task in the sheet by clicking the keyword link
 *
 * Props:
 *   task, issues (array of open issues), canResolve, onResolved
 */
export function IssueTaskBanner({ task, issues, canResolve, onResolved, onFocusTask, issuesBase, taskLabelKey }) {
  const [expanded, setExpanded] = useState(false);
  const [resolvingId, setResolvingId] = useState(null); // issue id being resolved
  const [resolveNote, setResolveNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openResolveDialog = (issueId) => {
    setResolvingId(issueId);
    setResolveNote("");
  };

  const submitResolve = async () => {
    if (!resolvingId) return;
    setBusy(true);
    try {
      await api.post(issuesBase ? `${issuesBase}/issues/${resolvingId}/resolve` : `/issues/${resolvingId}/resolve`, { resolution: resolveNote.trim() || undefined });
      toast.success("Issue resolved");
      setResolvingId(null);
      setResolveNote("");
      onResolved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const title = (taskLabelKey && task?.[taskLabelKey]) || task?.keyword || task?.title || "(untitled task)";
  const n = issues.length;

  return (
    <div
      className="issue-shake border border-rose-300 bg-rose-50/90 shadow-sm rounded-lg overflow-hidden"
      data-testid={`issue-banner-${task.id}`}
    >
      <div className="flex items-start gap-3 px-3 py-2">
        <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-rose-800">
              This task has {n === 1 ? "an issue" : `${n} issues`}, resolve ASAP!
            </span>
            <button
              onClick={() => onFocusTask?.(task.id)}
              className="text-xs text-rose-700 hover:text-rose-900 underline underline-offset-2 truncate max-w-[280px]"
              data-testid={`issue-banner-task-link-${task.id}`}
            >
              {title}
            </button>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-rose-700 hover:text-rose-900 flex items-center gap-1 shrink-0"
          data-testid={`issue-banner-toggle-${task.id}`}
        >
          {expanded ? <>Hide <ChevronUp className="h-3.5 w-3.5" /></> : <>Show <ChevronDown className="h-3.5 w-3.5" /></>}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-rose-200 bg-white/70 divide-y divide-rose-100">
          {issues.map((iss) => (
            <div key={iss.id} className="px-3 py-2.5" data-testid={`issue-banner-item-${iss.id}`}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="text-[11px] text-rose-700 font-medium">
                  Raised by {iss.created_by_name} • {formatWhen(iss.created_at)}
                </div>
                {canResolve && (
                  <Button
                    size="sm"
                    onClick={() => openResolveDialog(iss.id)}
                    className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5"
                    data-testid={`issue-resolve-btn-${iss.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolved
                  </Button>
                )}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{iss.description}</div>
              {iss.images && iss.images.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-1.5">
                  {iss.images.map((im) => (
                    <a href="#" onClick={(e) => e.preventDefault()} key={im.id} className="block">
                      <AuthedImg url={im.url} alt="screenshot" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!resolvingId} onOpenChange={(v) => !v && setResolvingId(null)}>
        <DialogContent className="max-w-md" data-testid="issue-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Resolve issue</DialogTitle>
            <DialogDescription className="text-xs">Add an optional note describing what you changed. The issue will move to the history.</DialogDescription>
          </DialogHeader>
          <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} rows={3} placeholder="e.g. Reassigned publisher and re-generated the draft link." data-testid="issue-resolution-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolvingId(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitResolve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="issue-resolution-submit">
              {busy ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Resolving…</>) : "Mark resolved"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * IssueHistoryPanel — the "Issues" tab inside a project. Shows every
 * issue (open + resolved) for the project, most-recent first.
 */
export function IssueHistoryPanel({ projectId, tasks, canResolve, onChange, issuesBase, taskLabelKey }) {
  const [rows, setRows] = React.useState(null); // null = loading
  const [statusFilter, setStatusFilter] = React.useState("all"); // all | open | resolved
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(issuesBase ? `${issuesBase}/issues` : `/projects/${projectId}/issues`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setRows([]);
    }
  }, [projectId, issuesBase]);

  React.useEffect(() => { load(); }, [load]);

  const submitResolve = async () => {
    if (!resolvingId) return;
    setBusy(true);
    try {
      await api.post(issuesBase ? `${issuesBase}/issues/${resolvingId}/resolve` : `/issues/${resolvingId}/resolve`, { resolution: resolveNote.trim() || undefined });
      toast.success("Issue resolved");
      setResolvingId(null);
      setResolveNote("");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const tasksMap = React.useMemo(() => {
    const m = {};
    (tasks || []).forEach((t) => { m[t.id] = t; });
    return m;
  }, [tasks]);

  const filtered = React.useMemo(() => {
    if (!rows) return [];
    if (statusFilter === "all") return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const counts = React.useMemo(() => {
    if (!rows) return { all: 0, open: 0, resolved: 0 };
    return {
      all: rows.length,
      open: rows.filter((r) => r.status === "open").length,
      resolved: rows.filter((r) => r.status === "resolved").length,
    };
  }, [rows]);

  if (rows === null) {
    return (
      <div className="p-6 space-y-3" data-testid="issue-history-skeleton">
        <CardBlockSkeleton count={4} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="issues-history-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          {[
            { key: "all", label: "All", count: counts.all },
            { key: "open", label: "Open", count: counts.open },
            { key: "resolved", label: "Resolved", count: counts.resolved },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition ${statusFilter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`issues-filter-${f.key}`}
            >
              {f.label} <span className="ml-1 text-slate-400 tabular-nums">{f.count}</span>
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-400 ml-auto">Auto-cleaned after 14 days • Images after 7 days</div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
          No {statusFilter === "all" ? "" : statusFilter} issues yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((iss) => {
            const t = tasksMap[iss.task_id];
            const label = t ? ((taskLabelKey && t[taskLabelKey]) || t.keyword || t.title || "(untitled task)") : "(deleted task)";
            const isOpen = iss.status === "open";
            return (
              <div
                key={iss.id}
                className={`rounded-lg border ${isOpen ? "border-rose-200 bg-rose-50/60" : "border-emerald-200 bg-emerald-50/50"} p-3.5`}
                data-testid={`issue-history-row-${iss.id}`}
              >
                <div className="flex items-start gap-2">
                  {isOpen ? <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[11px] font-bold uppercase tracking-wide ${isOpen ? "text-rose-700" : "text-emerald-700"}`}>
                        {isOpen ? "Open" : "Resolved"}
                      </span>
                      <span className="text-sm font-medium text-slate-800 truncate max-w-[420px]" title={label}>{label}</span>
                      <span className="text-[11px] text-slate-500">• Raised by {iss.created_by_name} on {formatWhen(iss.created_at)}</span>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{iss.description}</div>
                    {iss.images && iss.images.length > 0 && (
                      <div className="mt-2 grid grid-cols-6 gap-1.5 max-w-2xl">
                        {iss.images.map((im) => (<AuthedImg key={im.id} url={im.url} alt="screenshot" />))}
                      </div>
                    )}
                    {!isOpen && (
                      <div className="mt-2 text-xs text-emerald-700 bg-white/70 border border-emerald-200 rounded px-2 py-1.5">
                        <span className="font-semibold">Resolved</span> by {iss.resolved_by_name} on {formatWhen(iss.resolved_at)}
                        {iss.resolution && <div className="mt-0.5 text-slate-700">{iss.resolution}</div>}
                      </div>
                    )}
                  </div>
                  {isOpen && canResolve && (
                    <Button
                      size="sm"
                      onClick={() => { setResolvingId(iss.id); setResolveNote(""); }}
                      className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5"
                      data-testid={`issue-history-resolve-${iss.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolve
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!resolvingId} onOpenChange={(v) => !v && setResolvingId(null)}>
        <DialogContent className="max-w-md" data-testid="issue-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Resolve issue</DialogTitle>
            <DialogDescription className="text-xs">Add an optional note describing what you changed.</DialogDescription>
          </DialogHeader>
          <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} rows={3} placeholder="e.g. Fixed the broken link and re-published." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolvingId(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitResolve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Resolving…</>) : "Mark resolved"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
